import React, { useState } from 'react';
import AnnouncementBody from '../components/AnnouncementBody';
import AnnouncementBodyEditor from '../components/AnnouncementBodyEditor';
import {
  useListAllAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const sectionCard = (title, icon, children) => (
  <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.5rem' }}>
    <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
      <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>{title}</h5>
    </div>
    <div style={{ padding: '1.25rem' }}>{children}</div>
  </div>
);

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${brand.softGray}`,
  borderRadius: '6px', fontSize: '0.875rem', color: brand.deepBlue, outline: 'none',
  fontFamily: 'inherit',
};
const btnPrimary = {
  background: brand.deepBlue, color: 'white', border: 'none', borderRadius: '6px',
  padding: '8px 16px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', letterSpacing: '0.5px',
};
const btnOutline = {
  background: 'transparent', color: brand.tealBlue, border: `1px solid ${brand.tealBlue}`,
  borderRadius: '6px', padding: '6px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
};
const btnDanger = {
  background: 'transparent', color: brand.charcoal, border: `1px solid ${brand.charcoal}`,
  borderRadius: '6px', padding: '6px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
};

// Render YYYY-MM-DD date input from a possibly-null ISO datetime.
const toDateInputValue = (iso) => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const isExpired = (ann) => {
  if (!ann.expires_at) return false;
  return new Date(ann.expires_at) <= new Date();
};

const emptyDraft = { title: '', body: '', pinned: false, expires_at: '' };

export default function HR() {
  const { data: announcements = [], isLoading } = useListAllAnnouncementsQuery();
  const [createAnnouncement, createState] = useCreateAnnouncementMutation();
  const [updateAnnouncement, updateState] = useUpdateAnnouncementMutation();
  const [deleteAnnouncement] = useDeleteAnnouncementMutation();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);

  const isSaving = createState.isLoading || updateState.isLoading;

  const submitNew = async () => {
    if (!newDraft.title.trim()) return;
    await createAnnouncement({
      title: newDraft.title.trim(),
      body: newDraft.body,
      pinned: newDraft.pinned,
      expires_at: newDraft.expires_at || null,
    }).unwrap();
    setNewDraft(emptyDraft);
    setShowNewForm(false);
  };

  const startEditing = (ann) => {
    setEditingId(ann.id);
    setEditDraft({
      title: ann.title,
      body: ann.body,
      pinned: !!ann.pinned,
      expires_at: toDateInputValue(ann.expires_at),
    });
  };

  const submitEdit = async () => {
    if (!editDraft.title.trim()) return;
    await updateAnnouncement({
      id: editingId,
      title: editDraft.title.trim(),
      body: editDraft.body,
      pinned: editDraft.pinned,
      expires_at: editDraft.expires_at || null,
    }).unwrap();
    setEditingId(null);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return;
    await deleteAnnouncement({ id }).unwrap();
    if (editingId === id) setEditingId(null);
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>HR Portal</h4>
        <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>Manage content shown on the Employee Hub</p>
      </div>

      {sectionCard('Company Announcements', 'megaphone',
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={btnPrimary} onClick={() => setShowNewForm(v => !v)} disabled={isSaving}>
              <i className="bi bi-plus me-1" />{showNewForm ? 'Cancel' : 'New Announcement'}
            </button>
          </div>

          {showNewForm && (
            <div style={{ background: brand.softGray, borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <input style={{ ...inputStyle, marginBottom: '0.5rem' }} placeholder="Title"
                value={newDraft.title} onChange={e => setNewDraft(p => ({ ...p, title: e.target.value }))} />
              <AnnouncementBodyEditor
                value={newDraft.body}
                onChange={body => setNewDraft(p => ({ ...p, body }))}
                textareaStyle={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                accentColor={brand.tealBlue}
                hintColor={brand.charcoal}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: brand.charcoal, fontSize: '0.875rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newDraft.pinned}
                    onChange={e => setNewDraft(p => ({ ...p, pinned: e.target.checked }))} />
                  Pin to top
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: brand.charcoal, fontSize: '0.875rem' }}>
                  Expires:
                  <input type="date" style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
                    value={newDraft.expires_at} onChange={e => setNewDraft(p => ({ ...p, expires_at: e.target.value }))} />
                </label>
                <button style={btnPrimary} onClick={submitNew} disabled={isSaving || !newDraft.title.trim()}>
                  {isSaving ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>
          )}

          {isLoading && <p style={{ color: brand.charcoal, margin: 0 }}>Loading…</p>}
          {!isLoading && announcements.length === 0 && (
            <p style={{ color: brand.charcoal, margin: 0 }}>No announcements yet.</p>
          )}

          {announcements.map(ann => (
            <div key={ann.id} style={{ borderBottom: `1px solid ${brand.softGray}`, paddingBottom: '1rem', marginBottom: '1rem' }}>
              {editingId === ann.id ? (
                <>
                  <input style={{ ...inputStyle, marginBottom: '0.5rem' }} value={editDraft.title}
                    onChange={e => setEditDraft(p => ({ ...p, title: e.target.value }))} />
                  <AnnouncementBodyEditor
                    value={editDraft.body}
                    onChange={body => setEditDraft(p => ({ ...p, body }))}
                    textareaStyle={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                    accentColor={brand.tealBlue}
                    hintColor={brand.charcoal}
                  />
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: brand.charcoal, fontSize: '0.875rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editDraft.pinned}
                        onChange={e => setEditDraft(p => ({ ...p, pinned: e.target.checked }))} />
                      Pinned
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: brand.charcoal, fontSize: '0.875rem' }}>
                      Expires:
                      <input type="date" style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}
                        value={editDraft.expires_at} onChange={e => setEditDraft(p => ({ ...p, expires_at: e.target.value }))} />
                    </label>
                    <button style={btnPrimary} onClick={submitEdit} disabled={isSaving || !editDraft.title.trim()}>
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button style={btnDanger} onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '4px', flexWrap: 'wrap' }}>
                      {ann.pinned && <i className="bi bi-pin-fill" style={{ color: brand.goldenrod, fontSize: '0.75rem' }} />}
                      <span style={{ fontWeight: 700, color: brand.deepBlue }}>{ann.title}</span>
                      <span style={{ fontSize: '0.75rem', color: brand.charcoal }}>
                        {ann.created ? new Date(ann.created).toLocaleDateString() : ''}
                      </span>
                      {ann.expires_at && (
                        <span style={{
                          fontSize: '0.7rem',
                          color: isExpired(ann) ? '#a02020' : brand.charcoal,
                          background: isExpired(ann) ? 'rgba(160,32,32,0.08)' : 'rgba(0,0,0,0.04)',
                          padding: '1px 8px', borderRadius: '10px',
                          fontWeight: isExpired(ann) ? 700 : 400,
                        }}>
                          {isExpired(ann) ? 'Expired' : 'Expires'} {new Date(ann.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <AnnouncementBody
                      style={{ color: brand.charcoal, fontSize: '0.875rem' }}
                      linkColor={brand.tealBlue}
                    >
                      {ann.body || ''}
                    </AnnouncementBody>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <button style={btnOutline} onClick={() => startEditing(ann)}>Edit</button>
                    <button style={btnDanger} onClick={() => remove(ann.id)}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}

    </div>
  );
}
